import React, { Suspense, useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Box, Snackbar, Alert, Button } from '@mui/material';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import { useQueryClient } from '@tanstack/react-query';
import NavbarVisitor from '../components/visitor/NavbarVisitor/NavbarVisitor';
import BottomNav from '../components/visitor/BottomBar/BottomNav';
import StoreRouteFallback from '../components/global/StoreRouteFallback';
import EmptyContent from '../components/global/EmptyContent';
import ModelIntroductionScreen from '../components/global/ModelIntroductionScreen';
import { useHome } from '../hooks/Home/useHome';
import { useDarkMode } from '../context/DarkModeContext';
import { useSendStoreLogs } from '../hooks/StoreLogs/useSendStoreLogs';
import { useStoreSettings } from '../hooks/useStoreSettings';
import { StoreProvider } from '../context/StoreProvider';
import theme from '../utils/theme';
import notFound from '../assets/404/404.png';
import BACKEND_URL from '../config';
import { storeSync } from '../utils/storeSync';
import SeoHelmet from '../components/global/SeoHelmet';
import { applySeoMeta, buildStoreSeo, getAbsoluteUrl } from '../utils/seoMeta';
import { applyStoreBootBranding, hideBootSplash, isBootSplashActive } from '../utils/bootSplash';
import { clearPendingRequests } from '../utils/axios';
import { resumeStoreQueries } from '../utils/pwaResume';
import {
    persistStoreSession,
    restoreStoreSessionToLocalStorage,
    setStoreIdentity,
    watchStoreSessionIsolation,
} from '../utils/storeSession';

// ============================================================================
// 📌 CONSTANTS
// ============================================================================

const SW_PATH = '/service-worker.js';
const SW_UPDATE_CHECK_INTERVAL = 60000;

// ============================================================================
// 📌 PWA HELPER FUNCTIONS
// ============================================================================

/**
 * التحقق إذا كان التطبيق يعمل كـ PWA
 */
function isPWAMode() {
    return window.matchMedia('(display-mode: standalone)').matches ||
        window.navigator.standalone === true ||
        document.referrer.includes('android-app://');
}

/**
 * تنظيف شامل لبيانات المتجر السابق (لـ PWA)
 * Does NOT wipe slug-scoped localStorage — multi-store PWAs share origin storage;
 * deleting store-id-{prev} breaks Offers/Contact when returning to that store.
 * In-SPA store switches clear keys via cleanupStoreData instead.
 */
async function cleanupPWAData(currentSlug, prevSlug) {
    if (!isPWAMode()) return;

    // 1. تنظيف IndexedDB
    if ('indexedDB' in window) {
        try {
            const databases = await window.indexedDB.databases();
            for (const db of databases) {
                if (db.name && db.name.includes(prevSlug)) {
                    window.indexedDB.deleteDatabase(db.name);
                }
            }
        } catch (error) {
            console.error('[PWA] IndexedDB cleanup failed:', error);
        }
    }

    // 2. تنظيف Session Storage (per-tab — safe)
    const sessionKeys = Object.keys(sessionStorage);
    sessionKeys.forEach(key => {
        if (key.includes(prevSlug)) {
            sessionStorage.removeItem(key);
        }
    });

    // 3. تنظيف Cache Storage (slug-tagged only)
    if ('caches' in window) {
        const cacheNames = await caches.keys();
        for (const cacheName of cacheNames) {
            if (cacheName.includes(prevSlug)) {
                await caches.delete(cacheName);
            }
        }
    }
}

/**
 * PWA boot: update branding pointer for THIS store only.
 * Do NOT delete other stores' slug-scoped keys — multiple installed store PWAs
 * share origin localStorage; wiping store-id-{other} breaks Offers/Contact on resume.
 */
async function initPWACleanup(currentSlug) {
    if (!isPWAMode()) return;

    const currentInitialSlug = localStorage.getItem('initialStoreSlug');

    if (currentInitialSlug && currentInitialSlug !== currentSlug) {
        // Only clear shared branding pointers that are about to be replaced for this window.
        // Keep store-id-*, features_*, cart_*, etc. for other stores.
        try {
            localStorage.removeItem('initialStoreSlug');
            localStorage.removeItem('initialStoreLogo');
            // Legacy global (now also slug-scoped) — safe to clear; current store rewrites it
            localStorage.removeItem('store_network');
        } catch (_) {
            /* ignore */
        }

        // Optional: clear SW caches that are tagged with the previous slug only
        await cleanupPWAData(currentSlug, currentInitialSlug);
    }
}

// ============================================================================
// 📌 MAIN COMPONENT
// ============================================================================

const LayoutStore = () => {
    // ============================================================================
    // 🔹 REFS
    // ============================================================================

    const isInitialStoreSet = useRef(!!localStorage.getItem('initialStoreSlug'));
    const swRegistrationRef = useRef(null);
    const manifestUpdatedRef = useRef(false);
    const previousSlugRef = useRef(null);
    const pwaInitializedRef = useRef(false);

    // ✅ بديل redirectExecuted + localStorage flag
    const didInitialRedirectRef = useRef(false);
    const redirectInProgress = useRef(false);

    // ============================================================================
    // 🔹 HOOKS
    // ============================================================================

    const queryClient = useQueryClient();
    const { isDarkMode } = useDarkMode();
    const location = useLocation();
    const navigate = useNavigate();
    const { storeLogs: sendLogs } = useSendStoreLogs();
    const { slug } = useParams();
    const { home, isLoading, error } = useHome();

    // ============================================================================
    // 🔹 STATE
    // ============================================================================

    const [openIntroductionScreen, setOpenIntroductionScreen] = useState(false);
    const [showUpdateSnackbar, setShowUpdateSnackbar] = useState(false);

    // ============================================================================
    // 🔥 DEBOUNCED CLEANUP
    // ============================================================================

    const cleanupStoreData = useCallback((prevSlug, currentSlug) => {
        if (!prevSlug || prevSlug === currentSlug) return;

        console.log(`[Debounced Cleanup] Starting cleanup: ${prevSlug} -> ${currentSlug}`);

        // In-memory only for the previous store in THIS window.
        // Never delete other stores' slug-scoped localStorage keys — multi-store PWAs
        // share origin storage; wiping store-id-{prev} breaks Contact/Offers in that PWA.
        queryClient.removeQueries({
            predicate: (query) => {
                const key = query.queryKey;
                return Array.isArray(key) &&
                    key.includes(prevSlug) &&
                    !key.includes(currentSlug);
            }
        });

        try {
            sessionStorage.removeItem(`fl_store_session:${prevSlug}`);
        } catch (_) {
            /* ignore */
        }

        manifestUpdatedRef.current = false;
        swRegistrationRef.current = null;
        isInitialStoreSet.current = false;

        console.log(`[Debounced Cleanup] Completed for: ${prevSlug}`);
    }, [queryClient]);

    const debouncedCleanup = useMemo(() => {
        let timeoutId;
        return (prevSlug, currentSlug) => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(() => {
                cleanupStoreData(prevSlug, currentSlug);
            }, 500);
        };
    }, [cleanupStoreData]);

    // ============================================================================
    // 🔹 MEMOIZED VALUES
    // ============================================================================

    const currentTheme = useMemo(() => {
        const primaryColor = home?.data?.identity_color || "#3C1FAB";
        return theme(primaryColor, isDarkMode ? 'dark' : 'light');
    }, [home?.data?.identity_color, isDarkMode]);

    const introSettings = home?.data?.super_faster_settings?.introduction_screen;

    const hasIntroContent = useMemo(() => {
        return introSettings && (
            introSettings.description !== null ||
            introSettings.content !== null ||
            introSettings.image !== null
        );
    }, [introSettings]);

    const { isInitialized, settingsLoaded, settings } = useStoreSettings(
        home?.data,
        isLoading,
        slug
    );

    // ============================================================================
    // 🔹 CALLBACKS
    // ============================================================================

    const updateMetaTag = useCallback((name, content, attribute = 'name') => {
        let tag = document.querySelector(`meta[${attribute}="${name}"]`);
        if (!tag) {
            tag = document.createElement('meta');
            tag.setAttribute(attribute, name);
            document.head.appendChild(tag);
        }
        tag.setAttribute('content', content || '');
    }, []);

    const updateSEOTags = useCallback((data) => {
        if (!data) return;

        const seo = buildStoreSeo(data);
        // Prefer store logo for favicon; OG image may be cover
        const favicon = getAbsoluteUrl(data.logo || seo.image || notFound);

        applySeoMeta(seo);
        updateMetaTag('keywords', data.name || data.category?.name || '');
        updateMetaTag('author', data.name || '');
        updateMetaTag('language', 'ar');

        let icon = document.querySelector('link[rel="icon"]');
        if (!icon) {
            icon = document.createElement('link');
            icon.setAttribute('rel', 'icon');
            document.head.appendChild(icon);
        }
        icon.setAttribute('href', favicon);
    }, [updateMetaTag]);

    const updateManifest = useCallback(async (storeSlug) => {
        if (!storeSlug) return false;

        const manifestUrl = `${BACKEND_URL}/user/store/${storeSlug}/manifest.json`;

        try {
            const response = await fetch(manifestUrl, { cache: 'no-store' });

            if (!response.ok) {
                throw new Error(`HTTP ${response.status}: ${response.statusText}`);
            }

            const manifest = await response.json();

            const linkManifest = document.getElementById('web-manifest');
            if (linkManifest) {
                linkManifest.setAttribute('href', manifestUrl);
            }

            let metaTitle = document.querySelector('meta[name="apple-mobile-web-app-title"]');
            if (!metaTitle) {
                metaTitle = document.createElement('meta');
                metaTitle.setAttribute('name', 'apple-mobile-web-app-title');
                document.head.appendChild(metaTitle);
            }
            metaTitle.setAttribute('content', manifest.name || manifest.short_name || document.title);

            document.querySelectorAll('link[rel="apple-touch-icon"]').forEach(el => el.remove());

            if (manifest.icons && Array.isArray(manifest.icons)) {
                manifest.icons.forEach(icon => {
                    if (!icon.src || !icon.sizes) return;

                    const link = document.createElement('link');
                    link.setAttribute('rel', 'apple-touch-icon');
                    link.setAttribute('sizes', icon.sizes);
                    link.setAttribute('href', icon.src);
                    document.head.appendChild(link);
                });
            }

            if (manifest.theme_color) {
                let themeColor = document.querySelector('meta[name="theme-color"]');
                if (!themeColor) {
                    themeColor = document.createElement('meta');
                    themeColor.setAttribute('name', 'theme-color');
                    document.head.appendChild(themeColor);
                }
                themeColor.setAttribute('content', manifest.theme_color);
            }

            try {
                const icons = manifest.icons || [];
                let icon = '';
                let bestSize = 0;
                icons.forEach((item) => {
                    const size = parseInt(String(item.sizes || '0').split('x')[0], 10) || 0;
                    if (item.src && size >= bestSize) {
                        icon = item.src;
                        bestSize = size;
                    }
                });
                if (!icon && icons[0]?.src) icon = icons[0].src;

                const existing = JSON.parse(localStorage.getItem(`pwa-branding-${storeSlug}`) || '{}');
                const storedLogo = localStorage.getItem(`store-logo-${storeSlug}`) || '';
                // Keep a real store logo; don't let a weak manifest icon wipe it
                const isPlaceholder = (url) =>
                    !url ||
                    /avatar|placeholder|default[-_]?logo|default[-_]?user|no[-_]?image|ui-avatars|gravatar/i.test(
                        url
                    );
                const logo =
                    (!isPlaceholder(storedLogo) && storedLogo) ||
                    (!isPlaceholder(existing.logo) && existing.logo) ||
                    (!isPlaceholder(icon) && icon) ||
                    storedLogo ||
                    existing.logo ||
                    icon ||
                    '';

                localStorage.setItem(`pwa-branding-${storeSlug}`, JSON.stringify({
                    logo,
                    name: manifest.name || manifest.short_name || existing.name || storeSlug,
                    theme_color: manifest.theme_color || existing.theme_color || '#3C1FAB',
                    background_color: manifest.background_color || existing.background_color || '#ffffff',
                }));
                if (logo && !isPlaceholder(logo)) {
                    localStorage.setItem(`store-logo-${storeSlug}`, logo);
                }
            } catch (_) {
                /* ignore */
            }

            manifestUpdatedRef.current = true;
            return true;

        } catch (error) {
            console.error('[LayoutStore] Manifest update failed:', error);
            return false;
        }
    }, []);

    const registerServiceWorker = useCallback(async (storeSlug) => {
        if (!('serviceWorker' in navigator)) {
            console.warn('[LayoutStore] Service Worker not supported');
            return null;
        }

        try {
            const registration = await navigator.serviceWorker.register(SW_PATH, {
                scope: `/${storeSlug}/`,
                updateViaCache: 'none'
            });

            if (registration.active || registration.waiting || registration.installing) {
                const sw = registration.active || registration.waiting || registration.installing;

                if (sw.state === 'installing' || sw.state === 'waiting') {
                    await new Promise(resolve => {
                        sw.addEventListener('statechange', function handler(e) {
                            if (e.target.state === 'activated') {
                                sw.removeEventListener('statechange', handler);
                                resolve();
                            }
                        });
                    });
                }
            }

            if (navigator.serviceWorker.controller) {
                navigator.serviceWorker.controller.postMessage({
                    type: 'UPDATE_MANIFEST',
                    slug: storeSlug
                });
            }

            registration.addEventListener('updatefound', () => {
                const newWorker = registration.installing;

                newWorker.addEventListener('statechange', () => {
                    if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                        setShowUpdateSnackbar(true);
                    }
                });
            });

            return registration;

        } catch (error) {
            console.error('[LayoutStore] SW registration failed:', error);
            return null;
        }
    }, []);

    const handleSwUpdate = useCallback(() => {
        if (swRegistrationRef.current?.waiting) {
            swRegistrationRef.current.waiting.postMessage({ type: 'SKIP_WAITING' });

            navigator.serviceWorker.addEventListener('controllerchange', () => {
                window.location.reload();
            });
        }
        setShowUpdateSnackbar(false);
    }, []);

    // ============================================================================
    // 🔹 EFFECTS
    // ============================================================================

    /**
     * Soft-refetch Offers/Contact when returning from background (no full reload).
     * Also re-assert this window's store identity into shared localStorage first.
     */
    useEffect(() => {
        if (!slug) return undefined;

        let wasHidden = document.visibilityState === 'hidden';
        let lastResumeAt = 0;
        const RESUME_THROTTLE_MS = 1500;

        const runResume = () => {
            const now = Date.now();
            if (now - lastResumeAt < RESUME_THROTTLE_MS) return;
            lastResumeAt = now;
            restoreStoreSessionToLocalStorage(slug);
            clearPendingRequests();
            resumeStoreQueries(queryClient, slug);
        };

        const onVisibility = () => {
            if (document.visibilityState === 'hidden') {
                wasHidden = true;
                return;
            }
            if (document.visibilityState === 'visible' && wasHidden) {
                wasHidden = false;
                runResume();
            }
        };

        const onPageShow = (event) => {
            if (event.persisted || wasHidden) {
                wasHidden = false;
                runResume();
            }
        };

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pageshow', onPageShow);

        return () => {
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pageshow', onPageShow);
        };
    }, [slug, queryClient]);

    /**
     * Keep this PWA window's store-id/type/etc isolated from other store PWAs
     * that share origin localStorage.
     */
    useEffect(() => {
        if (!slug) return undefined;

        persistStoreSession(slug);

        return watchStoreSessionIsolation(slug, () => {
            clearPendingRequests();
            resumeStoreQueries(queryClient, slug);
        });
    }, [slug, queryClient]);

    /**
     * Effect 0: تنظيف PWA عند البدء
     */
    useEffect(() => {
        if (!pwaInitializedRef.current && isPWAMode()) {
            initPWACleanup(slug).then(() => {
                pwaInitializedRef.current = true;
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Effect 1: تنظيف localStorage عند تغيير slug (إبقاءه كما هو)
     */
    useEffect(() => {
        const prevSlug = localStorage.getItem('initialStoreSlug');

        if ((prevSlug && prevSlug !== slug) ||
            location.pathname === '/' ||
            location.pathname === '/join-network') {
            // لم نعد نعتمد على initialRedirectDone، لكن تنظيفه لا يضر
            localStorage.removeItem(`initialRedirectDone_${slug}`);
        }
    }, [slug, location.pathname]);

    /**
     * Bind boot splash / window slug to the CURRENT route only.
     * If this store has no cached branding → neutral spinner (never previous store logo).
     */
    useEffect(() => {
        if (!slug) return;
        applyStoreBootBranding(slug);
    }, [slug]);

    /**
     * Effect 2: تنظيف شامل عند تغيير المتجر (مع Debouncing)
     */
    useEffect(() => {
        const prevSlug = previousSlugRef.current;

        if (prevSlug && prevSlug !== slug) {
            console.log(`[Store Change] Detected: ${prevSlug} -> ${slug}`);
            debouncedCleanup(prevSlug, slug);
        }

        previousSlugRef.current = slug;
        sessionStorage.setItem('currentStoreSlug', slug);

    }, [slug, debouncedCleanup]);

    /**
     * ✅ Reset redirect ref عند تغيير slug (حتى لو داخل نفس الـ SPA)
     */
    useEffect(() => {
        didInitialRedirectRef.current = false;
        redirectInProgress.current = false;
    }, [slug]);

    /**
     * Effect 3: تخزين بيانات المتجر الأولي + PWA splash branding
     */
    useEffect(() => {
        if (!isLoading && home?.data && !isInitialStoreSet.current) {
            localStorage.setItem('initialStoreSlug', slug);
            localStorage.setItem('initialStoreLogo', home.data.logo);
            setStoreIdentity('store_network', home?.data?.store_network ?? '', slug);
            // Keep legacy global for older readers, but slug session is source of truth
            localStorage.setItem('store_network', home?.data?.store_network || '');
            isInitialStoreSet.current = true;
        }

        if (!isLoading && home?.data && slug) {
            const logo = home.data.logo || '';
            if (logo) {
                localStorage.setItem(`store-logo-${slug}`, logo);
            }
            try {
                const existing = JSON.parse(localStorage.getItem(`pwa-branding-${slug}`) || '{}');
                localStorage.setItem(`pwa-branding-${slug}`, JSON.stringify({
                    logo: logo || existing.logo || '',
                    name: home.data.name || existing.name || slug,
                    theme_color: home.data.identity_color || existing.theme_color || '#3C1FAB',
                    background_color: existing.background_color || '#ffffff',
                }));
            } catch (_) {
                /* ignore */
            }
        }
    }, [isLoading, home?.data, slug]);

    /**
     * Effect 4: تحديث localStorage + session isolation للمتجر الحالي
     */
    useEffect(() => {
        if (!isLoading && home?.data && slug) {
            setStoreIdentity('store-id', home.data.id, slug);
            setStoreIdentity('store-whats', home.data.cart_whatssapp_number, slug);
            setStoreIdentity('cart_active', home.data.cart_active, slug);
            setStoreIdentity('type', home.data.type || '', slug);
            setStoreIdentity('package_type', home.data.package_type || '', slug);
            setStoreIdentity('country-id', home.data.country?.id || '', slug);
            setStoreIdentity('features_subscriptions', home.data.features_subscriptions || [], slug);
            setStoreIdentity('liked', home.data.liked || 'false', slug);
            setStoreIdentity('store_network', home.data.store_network ?? '', slug);
            setStoreIdentity('has_private_package', home.data.has_private_package || 'false', slug);
            persistStoreSession(slug);
        }
    }, [isLoading, home?.data, slug]);

    /**
     * Effect 4.5: BroadcastChannel - مزامنة بيانات المتجر
     */
    useEffect(() => {
        if (!isLoading && home?.data && slug) {
            storeSync.getChannel(slug);
            storeSync.broadcastStoreId(slug, home.data.id);
            storeSync.broadcastStoreData(slug, {
                'store-whats': home.data.cart_whatssapp_number,
                'cart_active': home.data.cart_active,
                'type': home.data.type,
                'package_type': home.data.package_type,
                'country-id': home.data.country?.id,
                'features_subscriptions': home.data.features_subscriptions,
                'liked': home.data.liked
            });
        }

        return () => {
            if (slug) {
                storeSync.closeChannel(slug);
            }
        };
    }, [isLoading, home?.data, slug]);

    /**
     * Effect 5: تحديث SEO Tags
     */
    useEffect(() => {
        if (home?.data) {
            updateSEOTags(home.data);
        }
    }, [home?.data, updateSEOTags]);

    /**
     * Effect 6: جلب وتحديث Manifest
     */
    useEffect(() => {
        if (isLoading || !home?.data || manifestUpdatedRef.current) return;
        updateManifest(slug);
    }, [slug, home?.data, isLoading, updateManifest]);

    /**
     * Effect 7: تسجيل Service Worker
     */
    useEffect(() => {
        if (!('serviceWorker' in navigator) ||
            isLoading ||
            !home?.data ||
            !manifestUpdatedRef.current ||
            swRegistrationRef.current) {
            return;
        }

        const timer = setTimeout(async () => {
            const registration = await registerServiceWorker(slug);
            swRegistrationRef.current = registration;
        }, 500);

        return () => clearTimeout(timer);
    }, [slug, home?.data, isLoading, registerServiceWorker]);

    /**
     * Effect 8: فحص دوري لتحديثات SW
     */
    useEffect(() => {
        if (!swRegistrationRef.current) return;

        const interval = setInterval(() => {
            swRegistrationRef.current.update().catch(() => {
                // Silent fail
            });
        }, SW_UPDATE_CHECK_INTERVAL);

        return () => clearInterval(interval);
    }, []);

    /**
     * Effect 9: إرسال Store Logs
     */
    useEffect(() => {
        if (home?.data?.id) {
            sendLogs('views');
        }
    }, [sendLogs, home?.data?.id]);

    /**
     * Effect 10: تنظيف عند إغلاق الصفحة
     * (لم نعد نعتمد على initialRedirectDone، لكن إبقاءه كتظيف لا يسبب آثار جانبية)
     */
    useEffect(() => {
        const handleSessionEnd = () => {
            localStorage.removeItem(`initialRedirectDone_${slug}`);
            isInitialStoreSet.current = false;
        };

        window.addEventListener('beforeunload', handleSessionEnd);
        return () => window.removeEventListener('beforeunload', handleSessionEnd);
    }, [slug]);

    /**
     * Effect 11: إظهار شاشة المقدمة
     */
    useEffect(() => {
        if (introSettings?.active === 1 && hasIntroContent) {
            setOpenIntroductionScreen(true);
        }
    }, [hasIntroContent, introSettings]);

    /**
     * ✅ Effect 12+13 (Redirect Logic) - النسخة الجديدة
     * - لا نقرأ/نكتب initialRedirectDone في localStorage
     * - نعمل redirect فقط إذا المستخدم على المسار الأساسي /{slug}
     * - مرة واحدة لكل Mount (وبالتالي يتكرر عند Refresh/جلسة جديدة)
     */
    useEffect(() => {
        if (!isInitialized || !settingsLoaded || isLoading || !home?.data || !slug) return;
        if (didInitialRedirectRef.current) return;
        if (redirectInProgress.current) return;

        const normalizedPath = location.pathname.replace(/\/+$/, '');
        const basePath = `/${slug}`;

        // ✅ لا نعيد توجيه لو داخل صفحة فرعية
        if (normalizedPath !== basePath) {
            didInitialRedirectRef.current = true; // اعتبرنا أن "أول دخول" تحقق حتى لا نعمل redirect لاحقًا داخل نفس التشغيل
            return;
        }

        // لو default_page ليست products: لا نعمل redirect، فقط نؤمن عدم إعادة التنفيذ داخل نفس التشغيل
        if (settings?.default_page !== 'products') {
            didInitialRedirectRef.current = true;
            return;
        }

        redirectInProgress.current = true;

        const targetPath = home.data.type === 'mall'
            ? `${basePath}/shops`
            : `${basePath}/menu`;

        didInitialRedirectRef.current = true;
        navigate(targetPath, { replace: true });

        setTimeout(() => {
            redirectInProgress.current = false;
        }, 1000);

    }, [
        isInitialized,
        settingsLoaded,
        isLoading,
        home?.data,
        slug,
        location.pathname,
        settings?.default_page,
        navigate
    ]);

    /**
     * On error only — success path keeps HTML splash until the first page
     * content calls revealStoreUI() so we never flash Navbar/React loaders.
     */
    useEffect(() => {
        if (error) hideBootSplash();
    }, [error]);

    // Safety: never leave the boot splash stuck if a page forgets to reveal
    useEffect(() => {
        if (!isInitialized || !settingsLoaded || isLoading || !home?.data) return undefined;
        const timer = window.setTimeout(() => {
            if (isBootSplashActive()) hideBootSplash();
        }, 8000);
        return () => window.clearTimeout(timer);
    }, [isInitialized, settingsLoaded, isLoading, home?.data]);

    // ============================================================================
    // 🔹 RENDER
    // ============================================================================

    if (error) {
        return (
            <EmptyContent
                image={notFound}
                title={error?.response?.data?.message || 'حدث خطأ ما'}
            />
        );
    }

    if (!isInitialized || !settingsLoaded || isLoading) {
        // Single loader: HTML boot splash (or store fallback on SPA navigations)
        if (isBootSplashActive()) {
            return <Box sx={{ minHeight: '100dvh' }} />;
        }
        return <StoreRouteFallback isShow />;
    }

    const storeSeo = buildStoreSeo(home?.data);

    return (
        <>
            <SeoHelmet
                title={storeSeo.title}
                description={storeSeo.description}
                image={storeSeo.image}
                url={storeSeo.url}
                type={storeSeo.type}
                siteName={storeSeo.siteName}
                keywords={home?.data?.name || home?.data?.category?.name}
                author={home?.data?.name}
                language="ar"
            />
            <MuiThemeProvider theme={currentTheme}>
                <StoreProvider storeData={home?.data}>
                    <NavbarVisitor />
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        minHeight: '90dvh'
                    }}>
                        {/* Nested Suspense: lazy pages must NOT bubble to root and unmount this shell */}
                        <Suspense fallback={<StoreRouteFallback />}>
                            <Outlet />
                        </Suspense>
                    </Box>
                    <BottomNav />

                    <Snackbar
                        open={showUpdateSnackbar}
                        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                        sx={{ mb: 8 }}
                    >
                        <Alert
                            severity="info"
                            variant="filled"
                            action={
                                <Button
                                    color="inherit"
                                    size="small"
                                    onClick={handleSwUpdate}
                                    sx={{ fontWeight: 'bold' }}
                                >
                                    تحديث
                                </Button>
                            }
                            onClose={() => setShowUpdateSnackbar(false)}
                            sx={{
                                width: '100%',
                                fontFamily: 'URW DIN Arabic',
                                fontSize: 16
                            }}
                        >
                            نسخة جديدة متوفرة! اضغط لتحديث التطبيق.
                        </Alert>
                    </Snackbar>
                    {hasIntroContent && (
                        <ModelIntroductionScreen
                            openDialog={openIntroductionScreen}
                            setOpenDialog={setOpenIntroductionScreen}
                            data={introSettings}
                        />
                    )}
                </StoreProvider>
            </MuiThemeProvider>
        </>
    );
};

export default LayoutStore;
